import AuthStorage from '@common/services/authStorage';
import utils from '@helper/utils';
import axios from 'axios';
import { useEffect } from 'react';

// `Notification.permission` only reflects the *browser's* decision. If the
// user dismisses our own confirm() (or closes the native prompt without
// picking allow/deny), permission stays 'default' forever, so without a
// separate persisted flag this prompt would re-fire on every remount (e.g.
// the `window.location.reload()` calls elsewhere in the app after login).
const PROMPT_ASKED_STORAGE_KEY = 'pushNotificationPromptAsked';

const PushNotification = (): JSX.Element | null => {
  useEffect((): void => {
    const autoCheckPermission = async (): Promise<void> => {
      if (!('Notification' in window)) return;

      const alreadyAsked =
        localStorage.getItem(PROMPT_ASKED_STORAGE_KEY) === 'true';
      if (alreadyAsked) return;

      // Only ask if the user has NOT decided yet (status is 'default')
      if (Notification.permission === 'default') {
        // Record this before the user responds, so the prompt only ever
        // shows once regardless of whether they accept or deny it.
        localStorage.setItem(PROMPT_ASKED_STORAGE_KEY, 'true');

        // Show the alert prompt
        const userAgreed = window.confirm(
          "We'd like to notify you about notification job updates. Allow notifications?",
        );

        if (userAgreed) {
          await subscribeToPush();
        }
      }
    };

    const isLoggedIn = AuthStorage.getInstance().isAuthenticated();
    if (!isLoggedIn) return;

    autoCheckPermission();
  }, []);

  const subscribeToPush = async (): Promise<void> => {
    if (!('serviceWorker' in navigator)) return;

    // `pushManager.subscribe()` implicitly triggers the native permission
    // prompt if status is still 'default', but its return value doesn't
    // tell you what the user picked if they deny it (it just rejects).
    // Asking explicitly here makes the grant check unambiguous and lets
    // us bail out cleanly instead of catching a thrown error.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: utils.urlBase64ToUint8Array(
        process.env.REACT_APP_VAPID_PUBLIC_KEY || '',
      ),
    });

    // Ensure you send your auth token (e.g., Bearer token) in headers
    const token = AuthStorage.getInstance().getAccessToken();
    if (!token) return;

    await axios.post(
      `${process.env.REACT_APP_BASE_URL}/api/v1/notifications/push-subscribe`,
      subscription,
      {
        headers: {
          Authorization: token,
        },
      },
    );
  };

  return null;
};

export default PushNotification;
